import { Badge, Button, Card } from '@ohear/ui'
/**
 * Headphone test card.
 *
 * Three sequential checks: (1) 440 Hz tone in the LEFT ear only, (2) the
 * same tone in the RIGHT ear only, (3) a 200 Hz → 8 kHz logarithmic sweep
 * across both channels. After all three play, the actor confirms (Yes /
 * No) whether they heard everything cleanly. The Yes/No verdict is
 * persisted to `users.last_device_test_results.headphone` — onboarding
 * checks that the headphone test passed before the first record session.
 *
 * This is intentionally a sanity check, not a calibration tool. We don't
 * try to measure room response or compute frequency accuracy — we just
 * confirm channels aren't swapped and the headphones reproduce a
 * voice-band sweep without dropouts.
 *
 * All audio is synthesized in-browser with Web Audio (OscillatorNode +
 * StereoPannerNode), so no static assets ship with the app.
 */
import { useEffect, useRef, useState } from 'react'
import { useSaveDeviceTestResults } from '../../lib/api/account'

const TONE_DURATION_SEC = 1.5
const TONE_FREQUENCY_HZ = 440
const SWEEP_DURATION_SEC = 3
const SWEEP_START_HZ = 200
const SWEEP_END_HZ = 8000
const SWEEP_GAIN = 0.12 // a bit quieter than the L/R tones — the sweep covers more spectrum
const TONE_GAIN = 0.18

type Phase = 'idle' | 'left' | 'right' | 'sweep' | 'awaiting' | 'done'

export function HeadphoneTest() {
  const ctxRef = useRef<AudioContext | null>(null)
  const cancelRef = useRef<{ cancelled: boolean }>({ cancelled: false })
  const [phase, setPhase] = useState<Phase>('idle')
  const [verdict, setVerdict] = useState<'pass' | 'fail' | null>(null)
  const saveResults = useSaveDeviceTestResults()

  useEffect(() => {
    return () => {
      cancelRef.current.cancelled = true
      if (ctxRef.current && ctxRef.current.state !== 'closed') {
        void ctxRef.current.close()
      }
    }
  }, [])

  const ctx = (): AudioContext => {
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new AudioContext()
    }
    return ctxRef.current
  }

  const playPannedTone = (panValue: number, durationSec: number): Promise<void> => {
    return new Promise((resolve) => {
      const ac = ctx()
      const osc = ac.createOscillator()
      const gain = ac.createGain()
      const pan = ac.createStereoPanner()
      osc.frequency.value = TONE_FREQUENCY_HZ
      pan.pan.value = panValue
      osc.connect(gain).connect(pan).connect(ac.destination)
      const now = ac.currentTime
      // 20 ms attack / 50 ms release so we don't get a click on either edge.
      gain.gain.setValueAtTime(0, now)
      gain.gain.linearRampToValueAtTime(TONE_GAIN, now + 0.02)
      gain.gain.linearRampToValueAtTime(TONE_GAIN, now + durationSec - 0.05)
      gain.gain.linearRampToValueAtTime(0, now + durationSec)
      osc.start(now)
      osc.stop(now + durationSec)
      osc.onended = () => resolve()
    })
  }

  const playSweep = (durationSec: number): Promise<void> => {
    return new Promise((resolve) => {
      const ac = ctx()
      const osc = ac.createOscillator()
      const gain = ac.createGain()
      osc.connect(gain).connect(ac.destination)
      const now = ac.currentTime
      osc.frequency.setValueAtTime(SWEEP_START_HZ, now)
      osc.frequency.exponentialRampToValueAtTime(SWEEP_END_HZ, now + durationSec)
      gain.gain.setValueAtTime(0, now)
      gain.gain.linearRampToValueAtTime(SWEEP_GAIN, now + 0.05)
      gain.gain.linearRampToValueAtTime(SWEEP_GAIN, now + durationSec - 0.1)
      gain.gain.linearRampToValueAtTime(0, now + durationSec)
      osc.start(now)
      osc.stop(now + durationSec)
      osc.onended = () => resolve()
    })
  }

  const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

  const runSequence = async () => {
    setVerdict(null)
    cancelRef.current = { cancelled: false }
    const ac = ctx()
    if (ac.state === 'suspended') await ac.resume()

    setPhase('left')
    await playPannedTone(-1, TONE_DURATION_SEC)
    if (cancelRef.current.cancelled) return

    await wait(250)
    setPhase('right')
    await playPannedTone(1, TONE_DURATION_SEC)
    if (cancelRef.current.cancelled) return

    await wait(400)
    setPhase('sweep')
    await playSweep(SWEEP_DURATION_SEC)
    if (cancelRef.current.cancelled) return

    setPhase('awaiting')
  }

  const recordVerdict = (passed: boolean) => {
    setVerdict(passed ? 'pass' : 'fail')
    setPhase('done')
    saveResults.mutate({
      headphone: { passed, testedAt: new Date().toISOString() },
    })
  }

  const cancel = () => {
    cancelRef.current.cancelled = true
    setPhase('idle')
  }

  const playing = phase === 'left' || phase === 'right' || phase === 'sweep'
  const phaseLabel: Record<Phase, string> = {
    idle: '',
    left: 'Playing: LEFT ear (440 Hz)',
    right: 'Playing: RIGHT ear (440 Hz)',
    sweep: 'Playing: stereo sweep (200 Hz - 8 kHz)',
    awaiting: 'Did you hear all three?',
    done: '',
  }

  return (
    <Card title="Headphone test">
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-[var(--text-2)] m-0">
          Put on your headphones. We'll play three tones: left only, right only, then a smooth
          rising sweep across both ears. The whole thing takes about 7 seconds.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {phase === 'idle' || phase === 'done' ? (
            <Button
              variant="primary"
              onClick={() => {
                void runSequence()
              }}
            >
              {phase === 'done' ? 'Re-run test' : 'Play L / R / Sweep'}
            </Button>
          ) : phase === 'awaiting' ? (
            <>
              <Button
                variant="primary"
                onClick={() => {
                  recordVerdict(true)
                }}
              >
                Yes, all three
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  recordVerdict(false)
                }}
              >
                No, something was off
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
          )}

          {/* L / R indicator dots — pulse the side that's currently playing */}
          <div className="flex items-center gap-2 ml-1" aria-hidden="true">
            <Indicator label="L" active={phase === 'left' || phase === 'sweep'} />
            <Indicator label="R" active={phase === 'right' || phase === 'sweep'} />
          </div>

          {verdict ? (
            <Badge qcSeverity={verdict === 'pass' ? 'pass' : 'warn'}>
              {verdict === 'pass' ? 'Headphones look good' : 'Worth a second look'}
            </Badge>
          ) : null}
          {saveResults.isPending ? (
            <span className="text-[12px] text-[var(--text-2)]" aria-live="polite">
              Saving...
            </span>
          ) : null}
        </div>

        {playing || phase === 'awaiting' ? (
          <p
            aria-live="polite"
            role="status"
            className="text-[13px] text-[var(--text-1)] m-0 tabular-nums"
          >
            {phaseLabel[phase]}
          </p>
        ) : null}

        {verdict === 'fail' ? (
          <p className="text-[12px] text-[var(--text-2)] m-0">
            If a side was missing, your channels may be swapped — try the other earcup, or check
            your audio interface settings. If the sweep dropped out, the cable may be loose.
          </p>
        ) : null}
      </div>
    </Card>
  )
}

function Indicator({ label, active }: { label: 'L' | 'R'; active: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold tabular-nums transition-colors ${
        active ? 'bg-[var(--green)] text-white' : 'bg-[var(--bg-3)] text-[var(--text-2)]'
      }`}
    >
      {label}
    </span>
  )
}
